/* eslint-disable no-multi-assign */
/* eslint-disable no-param-reassign */
/* eslint-disable consistent-return */
/* eslint-disable no-console */
import * as Sentry from "@sentry/react"
import CryptoJS from "crypto-js"
import { createAsyncThunk } from "@reduxjs/toolkit"
import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from "axios"
import { ROOTS } from "hiya-common/config"
import { RootState, StoreType } from "hiya-common/src/stores"
import { useSelector } from "react-redux"
import { onlyToast } from "hiya-common/src/App/CustomToaster"
import { ApiHost } from "../constant"
import { isNode, compareVersion, isZY } from "./index"
import { logN } from "./log"
import { hAppEnum } from "hiya-common/src/reducers/env"

axios.defaults.timeout = 10000
class MyRequest {
  protected service: AxiosInstance = axios

  private static instance: MyRequest

  public IS_NODE: boolean

  constructor() {
    this.IS_NODE = isNode()
    this.service = axios.create(this.requestConfig())
    this.interceptorsRequest()
    this.interceptorsResponse()
  }

  public log: any
  public serverSignKey: string = ""

  setPublicFunction({ log, serverSignKey }: any) {
    this.log = log
    if (serverSignKey) {
      this.serverSignKey = serverSignKey
    }
  }

  /**
   * 初始化配置
   */
  protected requestConfig(): AxiosRequestConfig {
    if (this.IS_NODE) {
      return {
        baseURL: ROOTS.host[ENV],
        responseType: "json",
      }
    }
    return {
      baseURL: `${window.location.origin}/api/proxy?url=${ROOTS.host[ENV]}`,
      responseType: "json",
    }
  }

  private captureProxyRequestIssue(reason: string, extra?: Record<string, unknown>): void {
    if (this.IS_NODE) {
      return
    }
    const url = typeof extra?.url === "string" ? extra.url : ""
    // 只上报走 proxyPost 的代理异常
    if (!url.includes("/proxyPost")) {
      return
    }
    // 埋点 /stat/action 失败（超时等）噪音大，不上报 Sentry
    if (url.includes("/stat/action")) {
      return
    }
    Sentry.captureEvent({
      message: reason,
      level: "error",
      extra,
    })
  }

  /**
   * 请求拦截
   * 1.添加公共参数
   */
  protected interceptorsRequest(): void {
    this.service.interceptors.request.use(
      async (config: AxiosRequestConfig) => {
        if (config.store) {
          const {
            env: { deviceinfo, clientIp },
          } = config.store.getState()
          // 自动取deviceinfo
          Object.assign(config, {
            data: {
              web_ts: +new Date(),
              ...config.data,
              ...deviceinfo,
            },
          })
          if (clientIp) {
            // 携带用户真实ip
            config.headers["X-Forwarded-For"] = config.headers["X-Real-IP"] = clientIp
          }
        }

        let signDuration = 0
        let proxy_type: string | undefined = undefined

        if (this.IS_NODE) {
          // Node端直接使用服务端注入的签名
          if (this.serverSignKey) {
            const separator = config.url?.includes("?") ? "&" : "?"
            config.url = `${config.url}${separator}sign=${this.serverSignKey}`
          }
        } else {
          // suda 替换 api.in.mehiya.com -> api.in.imsuda.net
          if (config.data?.h_app?.toLowerCase() === hAppEnum.Suda && config.baseURL?.includes('api.in.mehiya.com')) {
            config.baseURL = config.baseURL.replace('api.in.mehiya.com', 'api.in.imsuda.net')
          }
          // 客户端：尝试获取代理和签名，内部有条件拦截
          try {
            const t0 = Date.now()
            const info = await getRequestInfo(config)
            signDuration = Date.now() - t0

            if (info) {
              const { request_sign, proxy_info } = info
              const hasProxy = proxy_info && proxy_info.ip && proxy_info.port

              if (hasProxy) {
                let originalBaseURL = config.baseURL || ROOTS.host[ENV]
                // 还原真正的请求目标 Host，去除本地 H5 开发/同域代理的前缀
                if (originalBaseURL.includes('/api/proxy?url=')) {
                  originalBaseURL = originalBaseURL.split('/api/proxy?url=')[1]
                }
                const targetUrl = originalBaseURL.replace(".in", "").replace("http:", ENV === "production" ? "https:" : "http:")
                config.baseURL = `http://${proxy_info.ip}:${proxy_info.port}/proxyPost?url=${targetUrl}`
                proxy_type = proxy_info.proxy_type
              } else if (request_sign) {
                // 如果没有代理才拼 sign，如果走了代理端上会自动拼 sign
                const separator = config.url?.includes("?") ? "&" : "?"
                config.url = `${config.url}${separator}sign=${request_sign}`
              }
            }
          } catch (e) {
            console.error("获取请求信息(代理/签名)失败:", e)
          }
        }

        Object.assign(config, {
          metadata: { startTime: new Date().valueOf(), signDuration, proxy_type },
        })
        
        // TODO: [测试专用] 调试完请删除这段 onConfigFinalized 逻辑
        if (typeof (config as any).onConfigFinalized === 'function') {
          (config as any).onConfigFinalized(config)
        }
        
        return config
      },
      (error) => {
        return Promise.reject(error)
      }
    )
  }

  /**
   * 响应拦截
   * 1.打印日志
   * 2.统一处理
   */
  protected interceptorsResponse(): void {
    this.service.interceptors.response.use(
      (response: AxiosResponse) => {
        return this.handleResponse(response)
      },
      (error) => {
        const { response } = error
        if (response === undefined) {
          const cfg: any = error?.config
          const extra: Record<string, unknown> = {
            phase: "network",
            errorMessage: error?.message ?? String(error),
          }
          if (cfg) {
            extra.url = `${cfg.baseURL ?? ""}${cfg.url ?? ""}`
            if (cfg.method) {
              extra.method = cfg.method
            }
            if (cfg.metadata?.proxy_type) {
              extra.proxy_type = cfg.metadata.proxy_type
            }
          }
          if (error?.code !== undefined) {
            extra.errorCode = error.code
          }
          this.captureProxyRequestIssue("HTTP请求异常(代理)", extra)
          return Promise.reject(new Error(error))
        }
        return this.handleResponse(response)
      }
    )
  }

  protected handleResponse(response: AxiosResponse): Promise<AxiosResponse<any>> {
    if (response.config.xcHideLog) {
      return Promise.resolve(response)
    }
    const { data, status, config, statusText } = response
    const succeed = data.ret === 1 && data.data
    this.responseLog(response, succeed)
    if (succeed) {
      return Promise.resolve(response)
    }
    const msg = data?.msg || data?.err_msg || data?.data?.err_msg
    if (response.config.xcErrorToast && msg) {
      onlyToast(msg)
      this.captureProxyRequestIssue("展示toast-接口业务错误(代理)", {
        phase: "business",
        url: `${config.baseURL}${config.url}`,
        httpStatus: status,
        statusText,
        bizMsg: msg,
        responseBody: data,
        ...((config as any).metadata?.proxy_type && { proxy_type: (config as any).metadata.proxy_type }),
      })
    }
    if (status !== 200) {
      this.captureProxyRequestIssue("接口业务错误(代理1)", {
        phase: "business",
        url: `${config.baseURL}${config.url}`,
        httpStatus: status,
        statusText,
        bizMsg: msg,
        responseBody: data,
        ...((config as any).metadata?.proxy_type && { proxy_type: (config as any).metadata.proxy_type }),
      })
    }
    this.captureProxyRequestIssue("接口业务错误(代理)", {
      phase: "business",
      url: `${config.baseURL}${config.url}`,
      httpStatus: status,
      statusText,
      bizMsg: msg,
      responseBody: data,
    })
    return Promise.reject(msg)
  }

  protected responseLog(response: AxiosResponse, succeed: boolean) {
    response.config.metadata.endTime = new Date().valueOf()
    const req = JSON.parse(response.config.data)
    const info: any = {
      mid: req.h_m,
      ip: response.config.headers["X-Forwarded-For"],
      type: this.IS_NODE ? "fetchData" : "http",
      url: `${response.config.baseURL}${response.config.url}`,
      method: response.config.method,
      req,
      res: response.data,
      status: response.status,
      ua: response.config.headers["User-Agent"],
      responseTime: response.config.metadata.endTime - response.config.metadata.startTime,
    }
    this.log[succeed ? "info" : "error"](info)
  }

  /**
   * 创建唯一实例（单例模式）
   */
  public static getInstance(): MyRequest {
    if (!this.instance) {
      this.instance = new MyRequest()
    }
    return this.instance
  }

  public post<T = any>(url: string, data: any = {}, config: AxiosRequestConfig = {}) {
    return this.service.post<T>(url, data, config)
  }

  public outPost<T = any>(url: string, data: any = {}, config: AxiosRequestConfig = {}) {
    return this.service.post<T>(url, data, {
      baseURL: ApiHost,
      headers: {
        "content-type": "application/x-www-form-urlencoded",
      },
      ...config,
    })
  }

  public get<T = any>(url: string, params: any = {}, config: AxiosRequestConfig = {}) {
    return this.service.get<T>(url, {
      params,
      ...config,
    })
  }
}

export const http = MyRequest.getInstance()
const logFun =
  ({ deviceinfo, clientIp, userAgent, queries, path }: RootState["env"]) =>
    ({
      action,
      otype,
      data,
      id,
      oid,
      src = "hiya",
      mid,
      did,
    }: {
      action: string
      otype: string
      data?: any
      id?: any
      oid?: any
      src?: string
      mid?: string
      did?: string
    }) => {
      const commonData = {
        app_name: "hiya",
        ip: clientIp,
        ua: userAgent,
        path,
        // 全部添加ab实验client_id  默认为 ‘’ 没有不穿设置为undefined
        // client_id: clientId || undefined,
        ...(queries.share_to && { shareTo: queries.share_to }),
        ...(queries.m && { mid: queries.m }),
        ...(queries.d && { did: queries.d }),
        ...(queries.nt && { netType: queries.nt }),
      }
      try {
        if (ENV !== "production") {
          logN.success("埋点", otype, {
            action,
            otype,
            data: {
              mid: deviceinfo.h_m,
              ...data,
              st: Date.now(),
              // eslint-disable-next-line no-plusplus
              logId: `${Math.floor(Date.now() / 1000)}`,
            },
            ...(id && { id }),
            ...(oid && { oid }),
            ...(src && { src }),
          })
        }
      } catch (e) {
        console.log(e)
      }
      return http.post(
        "/stat/action",
        {
          ...commonData,
          ...deviceinfo,
          list: [
            {
              action,
              otype,
              data: {
                mid: deviceinfo.h_m,
                ...data,
                st: Date.now(),
                // eslint-disable-next-line no-plusplus
                logId: `${Math.floor(Date.now() / 1000)}`,
                user_agent: userAgent,
              },
              ...(id && { id }),
              ...(oid && { oid }),
              ...(src && { src }),
            },
          ],
          ...(mid && { h_m: Number(mid) }),
          ...(did && { h_did: did }),
        },
        {
          baseURL: isNode() ? ApiHost : `${window.location.origin}/host`,
          headers: {
            "content-type": "application/x-www-form-urlencoded",
          },
          xcHideLog: true,
        }
      )
    }
export const log = createAsyncThunk<
  void,
  {
    action: string
    otype: string
    data?: any
    id?: any
    oid?: any
    src?: string
    mid?: string
    did?: string
  }
>("log", async (arg, store) => {
  await logFun(store.getState().env)(arg)
})
export function useLog() {
  const env = useSelector((state: any) => {
    return state.env
  })
  return logFun(env)
}

export {logFun}


const getRequestInfo = (config: AxiosRequestConfig): Promise<{
  request_sign?: string
  proxy_info?: {
    ip: string
    port: string
    proxy_type: string
  }
}> => {
  return new Promise((res) => {
    if (
      isZY() &&
      config.data?.h_dt === 0 &&
      config.data?.h_av && compareVersion(config.data.h_av, '4.55.1') &&
      ![hAppEnum.saada, hAppEnum.Suda].includes(config.data?.h_app)
    ) {
      const request_body = config.data ? CryptoJS.enc.Base64.stringify(CryptoJS.enc.Utf8.parse(JSON.stringify(config.data))) : ""
      const request_url = config.url || ""
      window.HiyaJSBridge.callHandler("getRequestInfo",  { request_body, request_url }, (info: any) => {
        res(info || {})
      })
    } else {
      res({
        request_sign:  new URLSearchParams(window.location.search).get("sign") || ""
      })
    }
  })
}
